export type FlatEntry = { id: string; key: string; value: string };

function parsePossibleStructure(input: string): any {
  if (typeof input !== 'string') return input;
  const trimmed = input.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return input;
  // Try JSON parse directly
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    // Try to convert single-quoted python-like dict to JSON
    try {
      const normalized = trimmed
        .replace(/'/g, '"')
        .replace(/None/g, 'null')
        .replace(/True/g, 'true')
        .replace(/False/g, 'false');
      return JSON.parse(normalized);
    } catch {
      return input;
    }
  }
}

// Transform flat entries into a one-level nested object map
// Supports: X -> value, X.Y -> { X: { Y: value } }, X.0 -> { X: [value] }
export function transformEntriesToMap(entries: FlatEntry[] | undefined): Record<string, any> {
  const result: Record<string, any> = {};
  if (!entries || entries.length === 0) return result;

  for (const e of entries) {
    const key = e.key;
    const value = e.value;
    if (key.includes('.')) {
      const [parent, child] = key.split('.', 1).concat(key.substring(key.indexOf('.') + 1));
      const index = Number(child);
      if (!Number.isNaN(index) && child.trim() !== '') {
        if (!Array.isArray(result[parent])) result[parent] = [];
        while (result[parent].length <= index) result[parent].push('');
        result[parent][index] = value;
      } else {
        if (typeof result[parent] !== 'object' || Array.isArray(result[parent]) || result[parent] === null) {
          result[parent] = {};
        }
        (result[parent] as Record<string, any>)[child] = value;
      }
    } else {
      if (key === 'order_to_evaluate') {
        const n = Number(value);
        result[key] = Number.isNaN(n) ? value : n;
      } else {
        const parsed = parsePossibleStructure(value);
        result[key] = parsed;
      }
    }
  }
  return result;
}

export function getEvaluationOrder(entries: FlatEntry[] | undefined): number | undefined {
  const map = transformEntriesToMap(entries);
  return typeof map.order_to_evaluate === 'number' ? map.order_to_evaluate : undefined;
}

